import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

const LanguageAndThemeToggle = () => {
  const { t } = useTranslation();
  const muiTheme = useMuiTheme();
  const { isDarkMode, toggleTheme } = useTheme();
  const { currentLanguage, changeLanguage, languages, getCurrentLanguageName, isRTL } = useLanguage();
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);

  const isLanguageMenuOpen = Boolean(languageAnchorEl);
  const subtleSurface = alpha(
    isDarkMode ? muiTheme.palette.common.white : muiTheme.palette.common.black,
    isDarkMode ? 0.07 : 0.045
  );
  const activeSurface = alpha(muiTheme.palette.primary.main, isDarkMode ? 0.22 : 0.11);

  const selectLanguage = (languageCode) => {
    changeLanguage(languageCode);
    setLanguageAnchorEl(null);
  };

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Tooltip title={isDarkMode ? t('lightMode', 'Light mode') : t('darkMode', 'Dark mode')}>
        <IconButton
          onClick={toggleTheme}
          color="inherit"
          aria-label={isDarkMode ? t('lightMode', 'Light mode') : t('darkMode', 'Dark mode')}
          sx={{
            width: 40,
            height: 40,
            borderRadius: 3,
            color: isDarkMode ? 'warning.light' : 'text.secondary',
            backgroundColor: subtleSurface,
            transition: 'background-color 180ms ease, transform 180ms ease',
            '&:hover': {
              backgroundColor: activeSurface,
              transform: 'translateY(-1px)',
            },
          }}
        >
          {isDarkMode ? <LightModeRoundedIcon fontSize="small" /> : <DarkModeRoundedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      <Button
        color="inherit"
        onClick={(event) => setLanguageAnchorEl(event.currentTarget)}
        aria-label={`${t('language', 'Language')}: ${getCurrentLanguageName()}`}
        aria-haspopup="menu"
        aria-expanded={isLanguageMenuOpen ? 'true' : undefined}
        startIcon={<TranslateRoundedIcon fontSize="small" />}
        endIcon={<KeyboardArrowDownRoundedIcon fontSize="small" />}
        sx={{
          minWidth: 0,
          minHeight: 40,
          px: 1.4,
          borderRadius: 3,
          color: 'inherit',
          backgroundColor: isLanguageMenuOpen ? activeSurface : 'transparent',
          fontSize: 14,
          fontWeight: 650,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          '& .MuiButton-startIcon': { marginInlineEnd: 0.75 },
          '& .MuiButton-endIcon': {
            marginInlineStart: 0.35,
            transition: 'transform 180ms ease',
            transform: isLanguageMenuOpen ? 'rotate(180deg)' : 'none',
          },
          '&:hover': { backgroundColor: activeSurface },
        }}
      >
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          {getCurrentLanguageName()}
        </Box>
      </Button>

      <Menu
        anchorEl={languageAnchorEl}
        open={isLanguageMenuOpen}
        onClose={() => setLanguageAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: isRTL ? 'left' : 'right' }}
        disableScrollLock
        slotProps={{
          paper: {
            sx: {
              minWidth: 190,
              mt: 1,
              p: 0.75,
              borderRadius: 3,
              border: `1px solid ${alpha(muiTheme.palette.divider, 0.8)}`,
              backgroundImage: 'none',
              boxShadow: '0 18px 48px rgba(10, 8, 28, 0.22)',
            },
          },
        }}
      >
        {languages.map((language) => {
          const isSelected = currentLanguage === language.code;
          return (
            <MenuItem
              key={language.code}
              selected={isSelected}
              onClick={() => selectLanguage(language.code)}
              sx={{
                minHeight: 44,
                borderRadius: 2.25,
                px: 1.25,
                '&.Mui-selected': {
                  color: 'primary.main',
                  backgroundColor: activeSurface,
                },
                '&.Mui-selected:hover': { backgroundColor: activeSurface },
              }}
            >
              <ListItemIcon sx={{ minWidth: 30 }}>
                {isSelected ? <CheckRoundedIcon color="primary" fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText primary={language.nativeName} secondary={language.name} />
            </MenuItem>
          );
        })}
      </Menu>
    </Stack>
  );
};

export default LanguageAndThemeToggle;
